import type { MyWorkspace } from "@minga/protocol"
import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { MachinesPill } from "@/features/runners/machines-pill"
import { useBootMark } from "@/lib/telemetry"
import { cn } from "@/lib/utils"
import { workspaceRoute } from "@/router"
import { AvatarMenu } from "./avatar-menu"
import { LogoMark } from "./logo"
import { NeedsYouChip } from "./needs-you-menu"
import { NotificationInboxButton } from "./notification-inbox"
import { ProjectSwitcher } from "./project-switcher"
import { PushHintCard } from "./push-hint"
import { VerifyBanner } from "./verify-banner"
import { WorkspacePicker } from "./workspace-picker"
import { WorkspaceSearch } from "./workspace-search"

/** Text tabs (refined-system §5.7): muted, the active one ink 600 on a 2px ink underline sitting on the bar's rule. */
const navLink =
  "tap -mb-px flex items-center self-stretch border-b-2 border-transparent text-muted no-underline hover:text-ink data-[status=active]:border-ink data-[status=active]:font-semibold data-[status=active]:text-ink"

/**
 * The left part of every header (spec §3.1): `minga· jung.dev ▾ / Dictly ▾`. `compact` hides the wordmark and the
 * workspace picker below 1024px (the project switcher lists the workspaces, decision 0056).
 */
export function HeaderLeft({
  workspace,
  project,
  compact = false,
}: {
  workspace: MyWorkspace
  project?: { readonly id: string; readonly name: string } | undefined
  compact?: boolean | undefined
}) {
  return (
    <div className="flex min-w-0 items-center gap-0.5">
      <Link
        to="/w/$workspaceId"
        params={{ workspaceId: workspace.id }}
        title={`${workspace.name}: home`}
        aria-label={`minga: ${workspace.name} home`}
        className={cn("tap mr-3 shrink-0 rounded-control max-sm:mr-1", compact && "max-lg:hidden")}
      >
        <LogoMark />
      </Link>
      <span className={cn("flex min-w-0 items-center", compact && "max-lg:hidden")}>
        <WorkspacePicker workspace={workspace} inProject={project !== undefined} />
      </span>
      {project && (
        <>
          <span aria-hidden="true" className={cn("text-muted", compact && "max-lg:hidden")}>
            /
          </span>
          <ProjectSwitcher workspaceId={workspace.id} projectId={project.id} name={project.name} />
        </>
      )}
    </div>
  )
}

export { NeedsYouChip } from "./needs-you-menu"

/** Header + page frame for workspace-level pages (home, settings). */
export function WorkspaceShell({
  children,
  project,
  wide = false,
}: {
  children: ReactNode
  /** Inside a project (its settings): the project switcher follows the workspace. */
  project?: { readonly id: string; readonly name: string } | undefined
  /** Full-width content (settings bring their own column layout). */
  wide?: boolean
}) {
  const { workspace } = workspaceRoute.useRouteContext()
  useBootMark("shell")
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b border-rule bg-paper">
        {/* UX pass 3 (M1): at 320–430 px the left part truncates (the picker is min-w-0) and never runs under the
            right group; tighter gaps on a phone */}
        <div data-workspace-header="" className="flex h-14 items-center gap-x-5 px-4 max-sm:gap-x-2 sm:px-7">
          <HeaderLeft workspace={workspace} project={project} />
          <nav aria-label="Workspace" className="hidden items-stretch gap-[22px] self-stretch md:flex">
            <Link
              to="/w/$workspaceId"
              params={{ workspaceId: workspace.id }}
              activeOptions={{ exact: true }}
              className={navLink}
            >
              Home
            </Link>
            {/* decision 0086: the workspace's people pages and guidelines */}
            <Link to="/w/$workspaceId/knowledge" params={{ workspaceId: workspace.id }} className={navLink}>
              Knowledge
            </Link>
            <Link to="/w/$workspaceId/settings" params={{ workspaceId: workspace.id }} className={navLink}>
              Settings
            </Link>
          </nav>
          <div className="ml-auto flex shrink-0 items-center gap-3 max-sm:gap-1">
            {/* UX pass phase 2 (P14): one header, so search sits here as it does in a project */}
            <WorkspaceSearch workspaceId={workspace.id} workspaceName={workspace.name} />
            {/* wave E (spec §7.4): "3 live", pushed; the project bar keeps its own line (refined-system §5.7) */}
            {/* on a phone: the glyph and the count (wave E review: a member with no machine sees what's live) */}
            <MachinesPill workspaceId={workspace.id} role={workspace.role} />
            <NeedsYouChip workspaceId={workspace.id} />
            <NotificationInboxButton workspaceId={workspace.id} />
            <AvatarMenu workspaceId={workspace.id} />
          </div>
        </div>
      </header>
      {/* wave E (spec §8): "Verify jannik@… to email invites and see invitations sent to you" while unverified */}
      <VerifyBanner role={workspace.role} />
      {/* decision 0113: on a phone, install Minga and turn on notifications, until done or dismissed */}
      <PushHintCard workspaceId={workspace.id} />
      <main className={cn("mx-auto w-full flex-1", wide ? "" : "max-w-6xl px-4 py-8 sm:px-6")}>
        {children}
      </main>
    </div>
  )
}

export function PageTitle({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end gap-4">
      <div className="min-w-0 flex-1">
        <h1 className="type-page">{title}</h1>
        {description && <p className="mt-1 text-muted">{description}</p>}
      </div>
      {children}
    </div>
  )
}
